import { cloneAsMutable, generateId, isIn, keepExisting, maybePipe, ObjectPatch, Pair } from '../util.js';
import { DomainId } from './domains.js';
import { Id, Index, Maybe, RosterEntry, Timestamp } from '../data.js';
import { defaultRosterEntry } from './roster.js';
import { Point, Product } from './products.js';
import { SanmarProductOption } from '../sanmar.js';
import { OrderItem } from './orders.js';
import { User } from './users.js';
import { Account } from './accounts.js';
import { ColorTriple, ColorTuple, MainColorGroup } from './colors.js';
import { Store } from './stores.js';

export interface TemplateCategory {
	id: string & { __entity?: 'TemplateCategory' };
	name: string;
	description: Maybe<string>;
	visible: boolean;
}

export interface Template {
	id: string & { __entity?: 'Template' };
	created: Timestamp;
	name?: string;
	public: boolean;
	admin_visible: boolean;
	priority: number;
	colors?: Record<string, ColorTuple>;
	product?: Id<Product>;
	domains: Maybe<string[]>;
	overrides: null | Partial<Record<DomainId, {
		priority?: Maybe<number>;
		name?: Maybe<string>;
		colors?: Record<string, ColorTuple>;
	}>>;
	categories: TemplateCategory['id'][];
	demo_stores?: Maybe<Record<DomainId, Id<Store>>>;
}

export interface ProductTemplate {
	
	id: string & { __entity?: 'ProductTemplate' };
	product: Id<Product>;
	template: Id<Template>;
	name?: string;
	visible: boolean;
	version: number;
	type: 'commands' | 'texture' | 'solid';
	commands?: Record<string, string>;
	sources?: Record<string, string>;
	colors?: Record<string, MainColorGroup[]>;
	points?: Record<string, Point>;
	texture?: string;
	color?: ColorTuple;
	color_lock?: Partial<Record<MainColorGroup, Maybe<ColorTuple>>>;
	texture_mask?: Maybe<string>;
	texture_multiplier?: Maybe<number>;
	sanmar_color_name?: string;
	created: Timestamp;
	
	demo_store_url?: Maybe<string>;
	template_info?: Template;
	sanmar_option?: SanmarProductOption;
	priority: number;
	overrides: null | Partial<Record<DomainId, { priority?: Maybe<number>; name?: Maybe<string> }>>;
	domains: Maybe<string[]>;
	
}

export interface ProductTemplateInventory {
	id: ProductTemplate['id'];
	inventory_sizes_in_stock?: string[];
	sizes_from_vendor?: string[];
	productSizesInStock?: Maybe<string[]>;
	productSizesFromVendor?: Maybe<string[]>;
}

export interface DesignData {

	id: string & { __entity?: 'DesignData' };
	previous: Maybe<string>;
	created: Timestamp;
	updated: Timestamp;
	domain: DomainId;
	
	product: Id<Product>;
	product_template: Maybe<string>;
	parts: Maybe<DesignParts>;
	changes: ObjectPatch<DesignParts>;
	roster_parts: Maybe<RosterPartType[]>;
	
	locked: boolean;
	
	legacy_hash: Maybe<string>;
	init_user: Maybe<Id<User>>;
	init_session: Maybe<string>;
	similarity: Maybe<number>;
	
}

export type Design = DesignData & { parts: DesignParts };

export interface SavedDesign {
	
	id: string & { __entity?: 'SavedDesign' };
	user: Maybe<Id<User>>;
	account: Maybe<Id<Account>>;
	session?: string;
	product: Id<Product>;
	design: Id<Design>;
	roster: RosterEntry[];
	created?: Timestamp;
	deleted?: boolean;
	autosave?: string;
	uploads: Id<Upload>[];
	product_template?: Id<ProductTemplate>;
	legacy_preview?: string;
	
	product_name?: string;
	vectorize_logos?: boolean;
	mask_image?: string;
	categories: string[];
	domain: DomainId;
	unpaid_uploads?: number;
	image_size?: Maybe<Pair<number>>;
	
}

export interface Upload {
	
	id: string & { __entity?: 'Upload' };
	user?: Maybe<Id<User>>;
	account?: Maybe<Id<Account>>;
	session?: string;
	hash: string;
	date: string;
	filename: string;
	vector: boolean;
	
	width?: number;
	height?: number;
	
	uploaded: Timestamp;
	deleted: boolean;
	
	restricted?: boolean;
	
	vector_upload?: string;
	vector_order_item?: number;
	vector_usable: boolean;
	vector_fake: boolean;
	
	commands?: string;
	commands_palette?: CommandsPalette;
	commands_aliases?: CommandsColorAliases;
	commands_width?: number;
	commands_height?: number;
	
	digitize_order_item?: number;
	digitize_usable: boolean;
	
	vector_upload_data?: Upload;
	account_domain: DomainId;
	
}

export interface DesignUpload extends Upload {
	colors?: ColorTuple[];
}

export interface DesignText {
	font: string;
	text?: string;
	stroke?: boolean;
	fill?: boolean;
	rotate?: number;
	sizeIn?: Maybe<number>;
}

export type DesignPartType = 'apparel' | 'text' | 'logo' | 'pw-logo' | 'name' | 'number' | 'alias' | 'flag';
export type RosterPartType = 'name' | 'number' | 'alias' | 'flag';
export type RosterTextPartType = 'name' | 'number' | 'alias';
export type TextPartType = 'text' | 'name' | 'number' | 'alias';

export const textPartTypes = ['text', 'name', 'number', 'alias'] as const;
export const rosterPartTypes = ['name', 'number', 'alias', 'flag'] as const;
export const movablePartTypes = ['logo', ...textPartTypes] as const;
export const rosterTextPartTypes = ['name', 'number', 'alias'] as const;

export const fontInchSizes = [2, 4, 6, 8, 10, 12];

export function isRosterPart(part: DesignPart): part is DesignPart & { type: RosterPartType } {
	return isIn(part.type, rosterPartTypes);
}
export function isRosterTextPart(part: DesignPart): part is DesignPart & { type: RosterTextPartType } {
	return isIn(part.type, rosterTextPartTypes);
}

export interface DesignPart {
	
	id: string & { __entity?: 'DesignPart' };
	type: DesignPartType;
	sort: number;
	
	/**
	 * @deprecated May still be present sometimes, needs fixing where possible.
	 */
	productDesign?: Maybe<ProductTemplate>;
	
	productTemplate?: Maybe<ProductTemplate>;
	
	upload?: DesignUpload;
	flag?: {
		country?: Maybe<string>;
		outline?: boolean;
		left?: 'on' | 'off' | string; // mirror/normal deprecated
		right?: 'on' | 'off' | string;
	};
	text?: DesignText;
	colors?: Maybe<Record<string, Maybe<ColorTuple>>>;
	threads?: Maybe<Record<string, Maybe<string>>>; // also used for SP color mapping
	position?: string;
	point?: Point;
	drawOverColor?: Maybe<MainColorGroup>;
	wrap?: boolean;
	tile?: string;
	cropToArea?: boolean;
	vector_upload?: DesignUpload;
	domain?: DomainId;
	productRevision?: Maybe<number>;
}

export type DesignParts = Readonly<Index<Readonly<DesignPart>>>;

export interface Font {
	id: string & { __entity?: 'Font' };
	name: string;
	filename: string;
	public: boolean;
	owners?: Maybe<Id<Account>[]>;
	preview: Maybe<string>;
	secret?: Maybe<string>;
	aliases: string[];
	created: Timestamp;
	updated: Timestamp;
}

export interface ThreadColor {
	id: string & { __entity?: 'ThreadColor' };
	created: Timestamp;
	updated: Timestamp;
	type: 'embroidery' | 'screen-print';
	name: string;
	reference?: Maybe<string>;
	rgb: ColorTriple;
	public: boolean;
	owners: Id<Account>[];
	priority: number;
	fallback?: Maybe<string>;
}

export type CommandsPaletteEntry = [
	r: number,
	g: number,
	b: number,
	id?: string,
];
export type CommandsPalette = CommandsPaletteEntry[];
export type CommandsColorAlias = [
	r: number,
	g: number,
	b: number,
	alias: number,
];
export type CommandsColorAliases = CommandsColorAlias[];
export type DesignRosterFields = { [K in RosterPartType]?: 'present' | 'allowed' | false };
export const getProductTemplateFromParts = (parts: DesignPart[] | DesignParts) => maybePipe(
	Array.isArray(parts) ? parts : [...Object.values(parts)],
	arr => arr.find(p => p.type == 'apparel'),
	p => p.productTemplate ?? p.productDesign,
	pt => pt.id,
);

const validPreviewWidthDef = [1366, 1024, 768, 512, 384, 256, 192, 128, 96] as const;
export const validPreviewWidths = validPreviewWidthDef as readonly number[];
export type ValidPreviewWidth = (typeof validPreviewWidthDef)[number];

export function applyRosterEntry(parts: DesignParts, entry: RosterEntry = defaultRosterEntry) {
	const clone = cloneAsMutable(parts);
	for (const part of Object.values(clone)) {
		if (part.type == 'flag' && part.flag) part.flag.country = entry.flag;
		for (const tt of rosterTextPartTypes) {
			if (part.type == tt && part.text) part.text.text = entry[tt] ?? '';
		}
	}
	return clone;
}

export function upgradeRoster(roster: RosterEntry[]) {
	return roster.map(re => re.id ? re : { ...re, id: generateId() });
}

export function getRosterSizes(item: OrderItem) {
	return [...new Set(keepExisting(item.roster!.map(re => re.size)))];
}

export function getDesignUploadIds(parts: DesignParts) {
	return [...new Set(keepExisting(Object.values(parts).map(part => part.upload?.id)))];
}

export function getDesignAreasUsed(parts: DesignParts) {
	return [...new Set(
		keepExisting(Object.values(parts).filter(p => p.type != 'apparel').map(p => p.point))
			.map(p => `${p.side}-${p.area}`)
	)].sort((a, b) => (
		(Number(b.startsWith('front-')) - Number(a.startsWith('front-')))
		|| (Number(b.endsWith('-main')) - Number(a.endsWith('-main')))
	));
}

export function calculatePartDPI(product: Product, part: DesignPart, upload: Upload): Maybe<number> {
	const ps = product.features?.physicalScale;
	if (!part.point || !part.point.w || !product.features?.areas || !upload.width || !ps) return null;
	const scale = ps.inches / ps.per;
	const partIn = scale * part.point.w;
	return Math.floor(upload.width / partIn);
}
